import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, TextField, Typography, Paper, Link, IconButton, CircularProgress } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { confirmSignUp } from "../services/authService";
import { getVerifyEmailParams } from '../utils/authQueryParams';
import { COGNITO_CONFIG } from "../../Utils/constants";
import { colors, typography } from '../../styles/theme';
import {
  authColors,
  authTitleSx,
  authSubtitleSx,
  authPrimaryButtonSx,
  authPanelFlexSx,
  authCloseButtonSx,
  authFieldErrorSx,
  authLinkAccentSx,
  authInfoAlertSx,
  authInfoAlertTextSx,
  otpDigitSx,
} from '../styles/authPanel.styles';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

const formatCooldown = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const verifyStatusTextSx = {
  fontFamily: typography.fontFamily.primary,
  fontSize: '16px',
  lineHeight: '22px',
  fontWeight: typography.fontWeight.regular,
  color: authColors.textSubtitle,
  textAlign: 'center',
};

const verifyTitleSx = {
  ...authTitleSx,
  mt: 0,
  mb: 0,
};

const verifySubtitleSx = {
  ...authSubtitleSx,
  mt: '12px',
  mb: 0,
  pr: 2,
};

const verifyPanelSx = {
  ...authPanelFlexSx,
  height: '100%',
  minHeight: 0,
  overflowY: 'auto',
};

const verifyContentSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  minHeight: '100%',
};

const verifyHeaderSx = {
  flexShrink: 0,
  width: '100%',
  mb: { xs: 2, sm: 2.5 },
};

const verifyBodySx = {
  flex: '1 0 auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  gap: { xs: 2.5, sm: 3 },
};

const verifyResendTextSx = {
  textAlign: 'center',
  fontFamily: typography.fontFamily.primary,
  fontSize: '16px',
  lineHeight: '20px',
  fontWeight: typography.fontWeight.regular,
  color: authColors.textWhite,
  mt: '24px',
};

function VerifyStatusPanel({ status }) {
  if (status === 'verifying') {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          py: '32px',
          px: '16px',
        }}
      >
        <CircularProgress size={44} thickness={4} sx={{ color: colors.primary }} />
        <Typography sx={{ ...verifyStatusTextSx, fontWeight: typography.fontWeight.medium, color: authColors.textWhite }}>
          Verificando código...
        </Typography>
        <Typography sx={verifyStatusTextSx}>
          Esto puede tardar unos segundos.
        </Typography>
      </Box>
    );
  }

  if (status === 'success') {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          py: '32px',
          px: '16px',
        }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: 52, color: colors.primaryLight }} />
        <Typography
          sx={{
            ...verifyStatusTextSx,
            fontWeight: typography.fontWeight.semibold,
            fontSize: '18px',
            color: authColors.textWhite,
          }}
        >
          ¡Verificación exitosa!
        </Typography>
        <Typography sx={verifyStatusTextSx}>
          Te redirigimos al login...
        </Typography>
      </Box>
    );
  }

  return null;
}

function OtpInput({ value, onChange, disabled }) {
  const inputsRef = useRef([]);

  const handleChange = (index, inputValue) => {
    const digit = inputValue.replace(/\D/g, '').slice(-1);
    const digits = value.split('');
    while (digits.length < CODE_LENGTH) digits.push('');
    digits[index] = digit;
    onChange(digits.join('').replace(/\s/g, '').slice(0, CODE_LENGTH));

    if (digit && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      const digits = value.split('');
      if (digits[index]) {
        digits[index] = '';
        onChange(digits.join(''));
        return;
      }
      if (index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    onChange(pasted);
    const focusIndex = Math.min(Math.max(pasted.length - 1, 0), CODE_LENGTH - 1);
    inputsRef.current[focusIndex]?.focus();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        gap: '10px',
      }}
    >
      {Array.from({ length: CODE_LENGTH }).map((_, index) => (
        <TextField
          key={index}
          inputRef={(element) => {
            inputsRef.current[index] = element;
          }}
          value={value[index] || ''}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          disabled={disabled}
          variant="outlined"
          inputProps={{
            maxLength: 1,
            inputMode: 'numeric',
            'aria-label': `Dígito ${index + 1} del código`,
          }}
          sx={otpDigitSx}
        />
      ))}
    </Box>
  );
}

function VerifyEmail() {
  const [error, setError] = useState("");
  const [resendSuccess, setResendSuccess] = useState(false);
  const [verifyStatus, setVerifyStatus] = useState('idle');
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const navigate = useNavigate();
  const location = useLocation();
  const verifyParams = useMemo(
    () => getVerifyEmailParams(
      new URLSearchParams(location.search),
      location.state,
      typeof window !== 'undefined' ? window.location.hash : '',
    ),
    [location.search, location.state]
  );

  const email = verifyParams.email;
  const [code, setCode] = useState(verifyParams.code);

  useEffect(() => {
    if (!email) {
      console.warn("No se proporcionó email para verificación. Redirigiendo a registro.");
      navigate("/register", { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => window.clearInterval(timerId);
  }, []);

  const isVerifying = verifyStatus === 'verifying';
  const isVerifySuccess = verifyStatus === 'success';
  const isVerifyBusy = isVerifying || isVerifySuccess;

  const canResend = resendCooldown <= 0 && !resending && !isVerifyBusy;

  const handleVerify = async (event) => {
    event.preventDefault();
    setVerifyStatus('verifying');
    setError("");
    setResendSuccess(false);

    try {
      if (!email) throw new Error('Email no proporcionado. Por favor regístrate nuevamente.');

      const result = await confirmSignUp(email, code);

      if (result.success) {
        setVerifyStatus('success');
        window.setTimeout(() => {
          navigate("/login", {
            state: { message: "¡Email verificado exitosamente! Ya puedes iniciar sesión." },
          });
        }, 2000);
        return;
      }

      console.error("Verification failed:", result.error);
      setError(result.error || "Código de verificación inválido");
      setVerifyStatus('idle');
    } catch (err) {
      console.error("Exception during verification:", err);
      setError(err.message || "Error al verificar código");
      setVerifyStatus('idle');
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    setError("");
    setResendSuccess(false);

    try {
      if (!email) throw new Error('Email no proporcionado');

      const params = {
        ClientId: COGNITO_CONFIG.clientId,
        Username: email,
      };

      const response = await fetch(
        `https://cognito-idp.${COGNITO_CONFIG.region}.amazonaws.com/`,
        {
          method: 'POST',
          headers: {
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.ResendConfirmationCode',
            'Content-Type': 'application/x-amz-json-1.1',
          },
          body: JSON.stringify(params),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setResendSuccess(true);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setTimeout(() => setResendSuccess(false), 4000);
      } else {
        throw new Error(result.message || 'Error al reenviar código');
      }
    } catch (err) {
      console.error("Error resending code:", err);
      setError(err.message || "Error al reenviar código");
    } finally {
      setResending(false);
    }
  };

  return (
    <Paper elevation={0} sx={verifyPanelSx}>
      <IconButton
        size="small"
        onClick={() => navigate("/app")}
        aria-label="Cerrar"
        sx={authCloseButtonSx}
      >
        <CloseIcon sx={{ fontSize: 22 }} />
      </IconButton>

      <Box sx={verifyContentSx}>
        <Box sx={verifyHeaderSx}>
          <Typography component="h1" sx={verifyTitleSx}>
            Verifica tu correo electrónico
          </Typography>
          <Typography component="p" sx={verifySubtitleSx}>
            Ingresa el código de verificación que enviamos a tu correo electrónico.
          </Typography>
          {email && (
            <Box sx={{ ...authInfoAlertSx, mt: '16px', mb: 0, width: '100%' }} role="status">
              <InfoOutlinedIcon sx={{ fontSize: 20, color: authColors.textWhite, flexShrink: 0 }} />
              <Typography sx={{ ...authInfoAlertTextSx, minWidth: 0, wordBreak: 'break-word' }}>
                Código enviado a <strong>{email}</strong>
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={verifyBodySx}>
          <Box sx={{ width: 'fit-content', maxWidth: '100%', mx: 'auto' }}>
            {isVerifyBusy ? (
              <VerifyStatusPanel status={verifyStatus} />
            ) : (
              <>
                <OtpInput value={code} onChange={setCode} disabled={isVerifying} />

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    mt: '14px',
                  }}
                >
                  <AccessTimeOutlinedIcon sx={{ fontSize: 16, color: authColors.textLight, flexShrink: 0 }} />
                  <Typography
                    sx={{
                      fontFamily: typography.fontFamily.primary,
                      fontSize: '12px',
                      lineHeight: '16px',
                      fontWeight: typography.fontWeight.regular,
                      color: authColors.textLight,
                    }}
                  >
                    El código expira en 24 horas.
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          <Box
            component="form"
            onSubmit={handleVerify}
            sx={{ width: '100%', flexShrink: 0 }}
          >
            {error && !isVerifyBusy && (
              <Typography sx={{ ...authFieldErrorSx, mb: '12px' }}>
                {error}
              </Typography>
            )}

            {!isVerifySuccess && (
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disableElevation
                disabled={code.length !== CODE_LENGTH || isVerifying}
                sx={authPrimaryButtonSx}
              >
                {isVerifying ? "Verificando..." : "Verificar"}
              </Button>
            )}

            {!isVerifyBusy && resendSuccess ? (
              <Box sx={{ ...authInfoAlertSx, mt: '24px' }} role="status">
                <InfoOutlinedIcon sx={{ fontSize: 20, color: authColors.textWhite, flexShrink: 0 }} />
                <Typography sx={authInfoAlertTextSx}>
                  Código reenviado. Revisá tu correo electrónico.
                </Typography>
              </Box>
            ) : !isVerifyBusy ? (
              <Typography sx={verifyResendTextSx}>
                ¿No recibiste el código?{' '}
                {resending ? (
                  <Box component="span" sx={{ fontWeight: typography.fontWeight.semibold }}>
                    Enviando...
                  </Box>
                ) : canResend ? (
                  <Link
                    component="button"
                    type="button"
                    onClick={handleResendCode}
                    sx={{
                      ...authLinkAccentSx,
                      fontSize: '16px',
                      verticalAlign: 'baseline',
                    }}
                  >
                    Solicitar uno nuevo
                  </Link>
                ) : (
                  <>
                    Podrás solicitar uno nuevo en{' '}
                    <Box component="span" sx={{ fontWeight: typography.fontWeight.semibold }}>
                      {formatCooldown(resendCooldown)}
                    </Box>
                    .
                  </>
                )}
              </Typography>
            ) : null}
          </Box>
        </Box>

      </Box>
    </Paper>
  );
}

export default VerifyEmail;
